/**
 * Ranked Explorer — unified field model.
 *
 * All four data sources (user attributes, assessmentTimeline entries, answers
 * inside user.data, and entries inside user.constructs / user.qualifiers) are
 * projected into a single Field abstraction. Both the editor and the runtime
 * resolve fields by the same id, extract values with the same function, and
 * apply the same filter semantics.
 *
 * Filter semantics:
 *   - Within a single facet: OR across selected option-ids and the
 *     "Not answered" sentinel.
 *   - Across multiple facets: AND.
 *   - An empty selection (no options, no none, no text) means the facet is
 *     inactive and matches everyone.
 */

export type FieldSource = "user" | "timeline" | "answer" | "construct";
export type FieldValueKind = "options" | "text" | "number" | "date" | "boolean";
export type DisplayType = "text" | "percentage" | "progress" | "bar";

export const DISPLAY_TYPES: Record<DisplayType, { label: string; hint: string }> = {
  text: { label: "Text", hint: "Raw value" },
  percentage: { label: "Percentage", hint: "Score as %" },
  progress: { label: "Progress", hint: "Bar + %" },
  bar: { label: "Bar only", hint: "Bar, no number" },
};

export function defaultDisplayType(field: Field | undefined): DisplayType {
  return field && field.kind === "number" ? "progress" : "text";
}

export function allowedDisplayTypes(field: Field | undefined): DisplayType[] {
  if (field && field.kind === "number") return ["percentage", "progress", "bar", "text"];
  return ["text"];
}

export interface FieldOption {
  id: string;
  name: string;
}

export interface Field {
  /** Stable identifier — used by configs to reference this field. */
  id: string;
  source: FieldSource;
  /** Auto-derived human label (configurable in the editor). */
  label: string;
  /**
   * Raw reference shown as monospace subtitle in the editor, used to
   * disambiguate fields with identical option sets.
   */
  ref: string;
  /** Inferred kind. UI may allow override between "options" and "text". */
  kind: FieldValueKind;
  /** Present when kind === "options". */
  options?: FieldOption[];
  /** Count of distinct observed values (used to suggest options vs text). */
  distinctCount?: number;
  /** Source-specific extraction metadata. Opaque to consumers. */
  meta: FieldMeta;
}

export type FieldMeta =
  | { kind: "user"; key: string }
  | { kind: "timeline"; key: string }
  | { kind: "answer"; bindingId: string; path: string; componentType?: string }
  | {
      kind: "construct";
      /** Stable id written by the automation, when present. */
      id?: string;
      /** Normalized name used as the primary match/group key. */
      normalizedName: string;
      description: string;
      name: string;
    };

export const NONE_OPTION_ID = "__none__";

export const SOURCE_META: Record<FieldSource, { label: string; icon: string; color: string }> = {
  user: { label: "User", icon: "lucide:user", color: "oklch(0.62 0.17 256)" },
  answer: { label: "Answer", icon: "lucide:message-square", color: "oklch(0.66 0.16 300)" },
  construct: { label: "Construct", icon: "lucide:bar-chart-3", color: "oklch(0.62 0.17 162)" },
  timeline: { label: "Timeline", icon: "lucide:clock", color: "oklch(0.7 0.13 70)" },
};

export const SOURCE_ORDER: FieldSource[] = ["user", "answer", "construct", "timeline"];

const USER_FIELDS_DENYLIST = new Set([
  "data",
  "constructs",
  "qualifiers",
  "assessmentTimeline",
  "answers",
  "raw",
  "id",
]);

/**
 * Normalize a construct/qualifier name so minor spelling variations across
 * candidates (case, whitespace, punctuation, "&" vs "and") collapse onto the
 * same field. Deliberately conservative — genuinely different names (e.g. a
 * renamed construct with extra qualifying text) stay distinct.
 */
export function normalizeConstructName(name: unknown): string {
  return String(name ?? "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

/** Coerce a score that may arrive as a number or a numeric string. */
function scoreToNumber(value: unknown): number | undefined {
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "" || isNaN(Number(trimmed))) return undefined;
    return Number(trimmed);
  }
  return undefined;
}

/** Loose detection of an ISO-like date string. */
const ISO_DATE_RE =
  /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

function inferKindFromValues(values: unknown[]): FieldValueKind {
  if (!values.length) return "text";
  const nonNull = values.filter((v) => v !== null && v !== undefined && v !== "");
  if (!nonNull.length) return "text";
  if (nonNull.every((v) => typeof v === "boolean")) return "boolean";
  if (nonNull.every((v) => typeof v === "number" || (typeof v === "string" && v !== "" && !isNaN(Number(v))))) {
    return "number";
  }
  if (nonNull.every((v) => typeof v === "string" && ISO_DATE_RE.test(v))) return "date";
  return "text";
}

function titleCase(value: string): string {
  return value
    .replace(/([A-Z])/g, " $1")
    .toLowerCase()
    .split(/[\s_\-./]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
    .trim();
}

function componentTypeOf(entry: any): string | undefined {
  return entry?.data?.type || entry?.data?.binding?.componentId;
}

function answerOptionIdsFromEntry(entry: any): string[] {
  const data = entry?.data?.data || {};
  const valRaw = entry?.data?.value;
  const componentType = componentTypeOf(entry) || "";

  // checkbox-group (multi-select)
  if (/checkbox-group/i.test(componentType)) {
    if (Array.isArray(data.options) && data.options.length) {
      return data.options.map((o: any) => String(o?.id ?? o?.value ?? o?.name ?? "")).filter(Boolean);
    }
    if (Array.isArray(valRaw)) return valRaw.map((v) => String(v)).filter(Boolean);
    return [];
  }

  // single-select (radio/select)
  if (data.option?.id !== undefined) return [String(data.option.id)];
  if (typeof valRaw === "string" && valRaw !== "") return [valRaw];
  if (typeof valRaw === "number") return [String(valRaw)];

  return [];
}

function answerTextValue(entry: any): string | undefined {
  const data = entry?.data?.data || {};
  if (typeof data.text === "string") return data.text;
  if (typeof entry?.data?.value === "string") return entry.data.value;
  return undefined;
}

function answerScoreValue(entry: any): number | undefined {
  const data = entry?.data?.data || {};
  if (typeof data.score === "number") return data.score;
  if (typeof entry?.data?.value === "number") return entry.data.value;
  return undefined;
}

function isAnswerEntry(entry: any): boolean {
  return entry?.type === "answer" && entry?.data;
}

function answerEntryKey(entry: any): { bindingId: string; path: string } | null {
  const bindingId = entry?.data?.binding?.id;
  if (!bindingId) return null;
  const path = String(entry?.data?.path || "");
  return { bindingId, path };
}

/** Build the field catalog from a set of users. */
export function discoverFields(users: any[]): Field[] {
  if (!Array.isArray(users) || !users.length) return [];

  const userValuesByKey = new Map<string, unknown[]>();
  const timelineValuesByKey = new Map<string, unknown[]>();
  const answerAcc = new Map<
    string,
    {
      bindingId: string;
      path: string;
      componentTypes: Set<string>;
      options: Map<string, string>;
      textValues: unknown[];
      hasOptions: boolean;
    }
  >();
  const constructAcc = new Map<
    string,
    { id?: string; description: string; name: string; scores: number[] }
  >();

  // constructs and qualifiers ("career fields") are surfaced as the same kind of
  // score field; scan both arrays into a single accumulator keyed by normalized name.
  const scanConstructLike = (arr: unknown) => {
    if (!Array.isArray(arr)) return;
    for (const c of arr) {
      if (!c?.name) continue;
      const name = String(c.name);
      const key = normalizeConstructName(name);
      if (!key) continue;
      const id = c.id !== undefined && c.id !== null && c.id !== "" ? String(c.id) : undefined;
      const description = String(c.description || "");
      let acc = constructAcc.get(key);
      if (!acc) {
        acc = { id, description, name, scores: [] };
        constructAcc.set(key, acc);
      } else {
        if (id && !acc.id) acc.id = id;
        // prefer a longer (more descriptive) description when it varies across users
        if (description.length > acc.description.length) acc.description = description;
      }
      const score = scoreToNumber(c.score);
      if (score !== undefined) acc.scores.push(score);
    }
  };

  for (const user of users) {
    if (!user || typeof user !== "object") continue;

    // user-level scalars
    for (const [k, v] of Object.entries(user)) {
      if (USER_FIELDS_DENYLIST.has(k)) continue;
      if (v === null || v === undefined) continue;
      const t = typeof v;
      if (t !== "string" && t !== "number" && t !== "boolean") continue;
      if (!userValuesByKey.has(k)) userValuesByKey.set(k, []);
      userValuesByKey.get(k)!.push(v);
    }

    // timeline scalars
    const timeline = user.assessmentTimeline;
    if (timeline && typeof timeline === "object") {
      for (const [k, v] of Object.entries(timeline)) {
        if (v === null || v === undefined) continue;
        const t = typeof v;
        if (t !== "string" && t !== "number" && t !== "boolean") continue;
        if (!timelineValuesByKey.has(k)) timelineValuesByKey.set(k, []);
        timelineValuesByKey.get(k)!.push(v);
      }
    }

    // answers
    if (Array.isArray(user.data)) {
      for (const entry of user.data) {
        if (!isAnswerEntry(entry)) continue;
        const key = answerEntryKey(entry);
        if (!key) continue;
        const fieldKey = `${key.path}::${key.bindingId}`;
        if (!answerAcc.has(fieldKey)) {
          answerAcc.set(fieldKey, {
            bindingId: key.bindingId,
            path: key.path,
            componentTypes: new Set(),
            options: new Map(),
            textValues: [],
            hasOptions: false,
          });
        }
        const acc = answerAcc.get(fieldKey)!;
        const ct = componentTypeOf(entry);
        if (ct) acc.componentTypes.add(ct);

        // pull options from binding (canonical) and from selected data
        const bindingOptions = entry?.data?.binding?.options;
        if (Array.isArray(bindingOptions) && bindingOptions.length) {
          acc.hasOptions = true;
          for (const opt of bindingOptions) {
            if (opt?.id && opt?.name) acc.options.set(String(opt.id), String(opt.name));
          }
        }
        const dataOptions = entry?.data?.data?.options;
        if (Array.isArray(dataOptions)) {
          for (const opt of dataOptions) {
            if (opt?.id && opt?.name) acc.options.set(String(opt.id), String(opt.name));
          }
        }
        const dataOption = entry?.data?.data?.option;
        if (dataOption?.id && dataOption?.name) {
          acc.options.set(String(dataOption.id), String(dataOption.name));
        }

        // text fallback
        const txt = answerTextValue(entry);
        if (txt !== undefined && txt !== "") acc.textValues.push(txt);
      }
    }

    // constructs + qualifiers (career fields)
    scanConstructLike(user.constructs);
    scanConstructLike(user.qualifiers);
  }

  const fields: Field[] = [];

  // user fields
  for (const [key, values] of userValuesByKey) {
    const distinct = new Set(values.map((v) => String(v)));
    const kind = inferKindFromValues(values);
    fields.push({
      id: `user:${key}`,
      source: "user",
      label: titleCase(key),
      ref: `user.${key}`,
      kind,
      distinctCount: distinct.size,
      options: undefined,
      meta: { kind: "user", key },
    });
  }

  // timeline fields
  for (const [key, values] of timelineValuesByKey) {
    const distinct = new Set(values.map((v) => String(v)));
    let kind = inferKindFromValues(values);
    // override well-known kinds
    if (key === "started" || key === "completed") kind = "date";
    if (key === "answerCount") kind = "number";
    fields.push({
      id: `timeline:${key}`,
      source: "timeline",
      label: titleCase(key),
      ref: `assessmentTimeline.${key}`,
      kind,
      distinctCount: distinct.size,
      meta: { kind: "timeline", key },
    });
  }

  // answer fields
  for (const [, acc] of answerAcc) {
    const fieldId = `answer:${acc.path}:${acc.bindingId}`;
    const componentType = Array.from(acc.componentTypes)[0];
    let kind: FieldValueKind;
    let options: FieldOption[] | undefined;
    if (acc.hasOptions && acc.options.size) {
      kind = "options";
      options = Array.from(acc.options.entries()).map(([id, name]) => ({ id, name }));
    } else if (acc.textValues.length) {
      kind = "text";
    } else {
      kind = "text";
    }
    const baseLabel = acc.bindingId || acc.path || "Answer";
    fields.push({
      id: fieldId,
      source: "answer",
      label: titleCase(baseLabel),
      ref: acc.path ? `${acc.path}/${acc.bindingId}` : acc.bindingId,
      kind,
      options,
      distinctCount: options?.length ?? new Set(acc.textValues.map(String)).size,
      meta: {
        kind: "answer",
        bindingId: acc.bindingId,
        path: acc.path,
        componentType,
      },
    });
  }

  // construct + qualifier fields (keyed by normalized name for stable, variation-tolerant ids)
  for (const [key, acc] of constructAcc) {
    fields.push({
      id: `construct:${key}`,
      source: "construct",
      label: acc.name,
      ref: acc.description ? `${acc.description} :: ${acc.name}` : acc.name,
      kind: "number",
      distinctCount: new Set(acc.scores).size,
      meta: {
        kind: "construct",
        id: acc.id,
        normalizedName: key,
        description: acc.description,
        name: acc.name,
      },
    });
  }

  fields.sort((a, b) => {
    const order: Record<FieldSource, number> = { user: 0, timeline: 1, answer: 2, construct: 3 };
    if (order[a.source] !== order[b.source]) return order[a.source] - order[b.source];
    return a.label.localeCompare(b.label);
  });

  return fields;
}

/**
 * Extract a comparable value for a field on a user.
 * Returns:
 *   - undefined: user has no value for this field ("not answered")
 *   - string[]: multi-select option ids
 *   - string | number | boolean: scalar
 */
export function getFieldValue(
  user: any,
  field: Field
): undefined | string | number | boolean | string[] {
  if (!user) return undefined;
  const meta = field.meta;

  if (meta.kind === "user") {
    const v = user[meta.key];
    if (v === null || v === undefined || v === "") return undefined;
    return v as any;
  }

  if (meta.kind === "timeline") {
    const v = user.assessmentTimeline?.[meta.key];
    if (v === null || v === undefined || v === "") return undefined;
    return v as any;
  }

  if (meta.kind === "answer") {
    if (!Array.isArray(user.data)) return undefined;
    const entry = user.data.find(
      (e: any) =>
        isAnswerEntry(e) &&
        e.data.binding?.id === meta.bindingId &&
        String(e.data.path || "") === meta.path
    );
    if (!entry) return undefined;

    if (field.kind === "options") {
      const ids = answerOptionIdsFromEntry(entry);
      if (!ids.length) return undefined;
      return ids;
    }
    if (field.kind === "number") {
      return answerScoreValue(entry);
    }
    const txt = answerTextValue(entry);
    if (txt === undefined || txt === "") {
      // fall back to first option id/name if any
      const ids = answerOptionIdsFromEntry(entry);
      if (ids.length === 1) return ids[0];
      if (ids.length > 1) return ids;
      return undefined;
    }
    return txt;
  }

  if (meta.kind === "construct") {
    const entries = [
      ...(Array.isArray(user.constructs) ? user.constructs : []),
      ...(Array.isArray(user.qualifiers) ? user.qualifiers : []),
    ];
    if (!entries.length) return undefined;
    const c = entries.find((x: any) => {
      if (!x) return false;
      // Prefer the stable id when both sides have one, else fall back to
      // normalized-name matching so minor spelling variations still resolve.
      if (meta.id && x.id !== undefined && x.id !== null && String(x.id) === meta.id) return true;
      return normalizeConstructName(x.name) === meta.normalizedName;
    });
    if (!c) return undefined;
    return scoreToNumber(c.score);
  }

  return undefined;
}

/** Render the value for display in a column. */
export function formatFieldValue(user: any, field: Field): string {
  const v = getFieldValue(user, field);
  if (v === undefined) return "";
  if (Array.isArray(v)) {
    if (field.options?.length) {
      const byId = new Map(field.options.map((o) => [o.id, o.name]));
      return v.map((id) => byId.get(id) ?? id).join(", ");
    }
    return v.join(", ");
  }
  if (typeof v === "string" && field.kind === "options" && field.options?.length) {
    const opt = field.options.find((o) => o.id === v);
    if (opt) return opt.name;
  }
  return String(v);
}

export interface FacetSelection {
  /** "options" facets use optionIds + includeNone; "text" facets use text + includeNone. */
  mode: "options" | "text";
  optionIds: string[];
  text: string;
  includeNone: boolean;
}

export function emptySelection(mode: "options" | "text" = "options"): FacetSelection {
  return { mode, optionIds: [], text: "", includeNone: false };
}

export function isSelectionActive(sel: FacetSelection): boolean {
  if (sel.includeNone) return true;
  if (sel.mode === "options") return sel.optionIds.length > 0;
  return sel.text.trim().length > 0;
}

/** Returns true if user passes the facet for this field given the selection. */
export function matchesFilter(user: any, field: Field, sel: FacetSelection): boolean {
  if (!isSelectionActive(sel)) return true;

  const val = getFieldValue(user, field);
  const isEmpty =
    val === undefined ||
    val === null ||
    (typeof val === "string" && val === "") ||
    (Array.isArray(val) && val.length === 0);

  if (sel.mode === "options") {
    if (isEmpty) return sel.includeNone;
    if (!sel.optionIds.length) return false;
    const vals = Array.isArray(val) ? val.map(String) : [String(val)];
    return vals.some((v) => sel.optionIds.includes(v));
  }

  // text mode
  if (isEmpty) return sel.includeNone;
  const q = sel.text.trim().toLowerCase();
  if (!q) return true;
  const stringVal = Array.isArray(val) ? val.join(" ") : String(val);
  return stringVal.toLowerCase().includes(q);
}

/** Score in 0..100 from raw value (handles 0..1 fractions). */
export function getScorePercent(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return n > 1 ? n : n * 100;
}

/**
 * Resolve a possibly-legacy field id to its canonical form.
 *
 * Construct/qualifier ids historically embedded the raw (unnormalized) name;
 * they are now keyed by the normalized name. Normalizing the tail of a stored
 * id lets saved configs keep matching after the switch. Non-construct ids are
 * already stable and returned unchanged.
 */
export function normalizeFieldId(fieldId: string): string {
  const idx = fieldId.indexOf(":");
  if (idx < 0) return fieldId;
  if (fieldId.slice(0, idx) !== "construct") return fieldId;
  return `construct:${normalizeConstructName(fieldId.slice(idx + 1))}`;
}

/**
 * Build a lookup that resolves both canonical and legacy field ids to a field.
 * Use everywhere a config's stored `fieldId` is turned back into a Field.
 */
export function buildFieldIndex(fields: Field[]): { get(fieldId: string): Field | undefined } {
  const exact = new Map<string, Field>();
  const normalized = new Map<string, Field>();
  for (const f of fields) {
    exact.set(f.id, f);
    const nId = normalizeFieldId(f.id);
    if (!normalized.has(nId)) normalized.set(nId, f);
  }
  return {
    get(fieldId: string): Field | undefined {
      if (fieldId == null) return undefined;
      return exact.get(fieldId) ?? normalized.get(normalizeFieldId(fieldId));
    },
  };
}

// --- Config types (shared between renderer and editor) ---

export interface FacetConfig {
  fieldId: string;
  label: string;
  mode: "options" | "text";
  defaultOpen: boolean;
  allowNone: boolean;
}

export interface ColumnConfig {
  fieldId: string;
  label: string;
  display: DisplayType;
  align: "left" | "right";
}

export interface GeneralConfig {
  individualReportPath: string;
  pageSize: number;
  sortFieldId: string;
  sortOrder: "asc" | "desc";
}

export interface ExplorerConfig {
  facets: FacetConfig[];
  columns: ColumnConfig[];
  general: GeneralConfig;
}

/** Map old scoreDisplay values to new DisplayType. */
function mapScoreDisplay(sd: string | undefined): DisplayType {
  if (sd === "bar-percent") return "progress";
  if (sd === "bar-only") return "bar";
  if (sd === "raw") return "percentage";
  return "progress";
}

/**
 * Normalize block definition data into the current ExplorerConfig shape.
 * Handles backward compat with the old flat `fields[]` format.
 */
export function normalizeConfig(raw: any): ExplorerConfig {
  if (!raw) {
    return {
      facets: [],
      columns: [],
      general: { individualReportPath: "", pageSize: 25, sortFieldId: "", sortOrder: "desc" },
    };
  }

  // New format: has facets/columns/general
  if (Array.isArray(raw.facets) || Array.isArray(raw.columns)) {
    return {
      facets: (raw.facets || []) as FacetConfig[],
      columns: (raw.columns || []) as ColumnConfig[],
      general: {
        individualReportPath: raw.general?.individualReportPath || raw.individualReportPath || "",
        pageSize: raw.general?.pageSize || raw.pageSize || 25,
        sortFieldId: raw.general?.sortFieldId || raw.defaultSortFieldId || "",
        sortOrder: raw.general?.sortOrder || raw.defaultSortOrder || "desc",
      },
    };
  }

  // Old format: flat fields[] array
  if (Array.isArray(raw.fields)) {
    const facets: FacetConfig[] = [];
    const columns: ColumnConfig[] = [];

    for (const f of raw.fields) {
      if (f.filter) {
        facets.push({
          fieldId: f.id,
          label: f.label || "",
          mode: f.filter.mode || "options",
          defaultOpen: true,
          allowNone: true,
        });
      }
      if (f.column) {
        columns.push({
          fieldId: f.id,
          label: f.label || "",
          display: mapScoreDisplay(f.column.scoreDisplay),
          align: "left",
        });
      }
    }

    return {
      facets,
      columns,
      general: {
        individualReportPath: raw.individualReportPath || "",
        pageSize: raw.pageSize || 25,
        sortFieldId: raw.defaultSortFieldId || "",
        sortOrder: raw.defaultSortOrder || "desc",
      },
    };
  }

  return {
    facets: [],
    columns: [],
    general: {
      individualReportPath: raw.individualReportPath || "",
      pageSize: raw.pageSize || 25,
      sortFieldId: raw.defaultSortFieldId || "",
      sortOrder: raw.defaultSortOrder || "desc",
    },
  };
}
